import {zodResolver} from '@hookform/resolvers/zod';
import React, {useEffect, useState, useMemo} from 'react';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {SORT_DEFAULT} from '../../../../constants';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {ANONYMOUS_PRESENTER_VALUE, CreateRoomFormType, createRoomValidator} from '../../../../incase-domain/validators/room/room';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import ReactHookFormInputFieldWithCounter from '../../../components/reactHookFormComponents/ReactHookFormInputFieldWithCounter';
import ReactHookFormDateTimeField from '../../../components/reactHookFormComponents/ReactHookFormDateTimeField';
import {useCreateRoom, useFetchPresentations} from '../queries';
import {presentationOptions} from '../utils/mappers';
import Button from '../../../../incase-domain/components/button/Button';
import ReactHookFormSelect from '../../../components/reactHookFormComponents/ReactHookFormSelect';
import ProtectionForm from '../../../../components/presentation/settings/ProtectionForm';
import SelectPresenter from '../components/SelectPresenter';
import {IPresentation} from '../../../../incase-domain/presentation';
import {ICreatedRoom} from '../../../../incase-domain/rooms/room.editor';

interface CreateRoomModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    presentation?: IPresentation;
    onRoomCreated?: (room: ICreatedRoom) => void;
}

function CreateRoomModal({open, setOpen, presentation, onRoomCreated}: CreateRoomModalProps): JSX.Element {
    const {t} = useTranslation();

    const user = useSelector(loggedInUserSelector);

    const [presentationSearchTerm, setPresentationSearchTerm] = useState('');
    const [presenterSearchTerm, setPresenterSearchTerm] = useState<string>('');

    const {
        data: presentationData,
        hasNextPage,
        fetchNextPage,
    } = useFetchPresentations({
        searchTerm: presentationSearchTerm,
        sortBy: SORT_DEFAULT,
        enabled: open,
    });

    const handleScrollToBottom = (): void => {
        if (hasNextPage) fetchNextPage();
    };

    const {mutate, isLoading} = useCreateRoom();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const useFormReturn: UseFormReturn<any, unknown> = useForm<CreateRoomFormType>({
        resolver: zodResolver(createRoomValidator),
        defaultValues: {
            roomName: '',
            protection: {types: []},
            presentationId: '',
            presenterId: user?._id || '',
            presenterEmail: '',
            startTime: '',
            endTime: '',
        },
    });

    const {
        control,
        handleSubmit,
        formState: {errors},
        reset,
        watch,
        setValue,
    } = useFormReturn;

    const onSubmit: SubmitHandler<CreateRoomFormType> = (data) => {
        if (isLoading) {
            return;
        }
        const transformedData = {...data};
        if (transformedData.presenterId === (user?._id as string) || transformedData.presenterId !== ANONYMOUS_PRESENTER_VALUE) {
            transformedData.presenterEmail = '';
        }
        if (transformedData.presenterId === ANONYMOUS_PRESENTER_VALUE) {
            transformedData.presenterId = '';
        }
        transformedData.startTime = data.startTime || undefined;
        transformedData.endTime = data.endTime || undefined;
        mutate(transformedData, {
            onSuccess: (createdRoom: ICreatedRoom) => {
                onRoomCreated?.(createdRoom);
                handleCancel();
            },
        });
    };

    const handleCancel = (): void => {
        reset();
        setOpen(false);
    };

    const protection = watch('protection');
    const presenterId = watch('presenterId');
    const presenterEmail = watch('presenterEmail');

    useEffect(() => {
        if (presenterEmail && presenterId?.length) {
            if (presenterId !== ANONYMOUS_PRESENTER_VALUE) {
                setValue('presenterEmail', '');
            }
        }
    }, [presenterEmail, setValue, presenterId]);

    useEffect(() => {
        if (protection.types.length === 0) {
            setValue('presenterId', '');
            setValue('presenterEmail', '');
            return;
        }

        if (!presenterId && user?._id) {
            setValue('presenterId', user._id);
        }
    }, [presenterId, protection, setValue, user?._id]);

    useEffect(() => {
        if (presentation) {
            setValue('presentationId', presentation._id);
        }
    }, [presentation, setValue]);

    const preselectedPresentationOption = useMemo(() => {
        if (presentation) {
            return [{label: presentation.name || '', value: presentation._id}];
        }
        return undefined;
    }, [presentation]);

    return (
        <Modal
            isOpen={open}
            onCancel={handleCancel}
            size="md"
            header={`${t('roomsPage.createRoom')}`}
            description={`${t('roomsPage.defineRoomPreferences')}`}
            icon="solar:play-circle-bold"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="c-room_modal">
                <div className="c-room_modal__input u-mb-2">
                    <ReactHookFormInputFieldWithCounter
                        text={`${t('roomsPage.modals.roomName')}`}
                        fieldName="roomName"
                        errors={errors}
                        control={control}
                        maxLength={40}
                        className="c-sidepanel__input"
                        textClassName="c-room_modal__input__text"
                        dataCy="room_name"
                    />
                </div>
                <div className="c-room_modal__input u-mb-4 u-flex u-flex-col">
                    <div className="u-flex u-h-between u-v-center c-room_modal__input__text">
                        <div>{`${t('roomsPage.createRoomModal.presentation')}`}</div>
                    </div>
                    <ReactHookFormSelect
                        fieldName="presentationId"
                        options={preselectedPresentationOption || presentationOptions(presentationData)}
                        useForm={useFormReturn}
                        placeholder={`${t('roomsPage.modals.selectPresentation')}`}
                        setSearchTerm={setPresentationSearchTerm}
                        disabled={!!presentation}
                        dataCy="presentation_select"
                        onScrollToBottom={handleScrollToBottom}
                    />
                </div>
                <div className="c-room_modal__input u-mb-4 u-flex u-flex-col u-gap-2">
                    <div className="c-room_modal__input__text">{`${t('roomsPage.modals.schedule')}`}</div>
                    <ReactHookFormDateTimeField
                        text={`${t('roomsPage.modals.startTime')}`}
                        fieldName="startTime"
                        control={control}
                        errors={errors}
                        textClassName="c-room_modal__input__text"
                        disablePast
                        dataCy="room_start_time"
                    />
                    <ReactHookFormDateTimeField
                        text={`${t('roomsPage.modals.endTime')}`}
                        fieldName="endTime"
                        control={control}
                        errors={errors}
                        textClassName="c-room_modal__input__text"
                        disablePast
                        minDateTime={watch('startTime')}
                        dataCy="room_end_time"
                    />
                </div>
                <div className="c-room_modal__input u-mb-4 u-flex u-flex-col">
                    <div className="u-flex u-h-between u-v-center c-room_modal__input__text">
                        <div>{`${t('roomsPage.modals.roomAccess')}`}</div>
                    </div>
                    <ProtectionForm protection={protection} setProtection={(value) => setValue('protection', value)} errors={errors} />
                </div>
                {protection.types.length > 0 && (
                    <div className="c-room_modal__input u-flex u-flex-col">
                        <div className="u-flex u-h-between u-h-left u-v-center c-room_modal__input__text">
                            <div>{`${t('roomsPage.createRoomModal.presenter')}`}</div>
                        </div>
                        <SelectPresenter
                            presenterId={presenterId}
                            presenterSearchTerm={presenterSearchTerm}
                            setPresenterSearchTerm={setPresenterSearchTerm}
                            useForm={useFormReturn}
                        />
                    </div>
                )}
                <div className="c-room_modal__buttons u-flex u-h-right u-gap-2">
                    <Button variant="soft" size="md" color="secondary" fullWidth onClick={handleCancel}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="solid"
                        size="md"
                        color="secondary"
                        fullWidth
                        dataCy="save_room"
                        loading={isLoading}
                        disabled={isLoading}
                        type="submit"
                    >
                        {t('common.save')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

export default CreateRoomModal;
